import React from "react";
import {
    Alert,
    Attachment,
    CloseIcon,
    Divider,
    Dropdown,
    ExclamationTriangleIcon,
    Flex,
    InputLabel,
    QuestionCircleIcon,
    Skeleton,
    Text,
    Tooltip,
} from "@fluentui/react-northstar";
import styled from "styled-components";
import DropzoneGlobal from "../../../../components/dropzone/DropzoneGlobal";
import { file_size_format } from "../../../../constants/maths";
import { ICON_FROM_EXTENSION } from "../../../../utils/functions";
import { Controller, useFormContext } from "react-hook-form";
import { useQuery } from "react-query";
import { _api_get_categories, _api_get_employees } from "../../api/index.hr";
import {
    AVATAR_DEFAULT,
    AVATAR_DEPARTMENT_DEFAULT,
} from "../../../../constants";

/**
 * Component for rendering the content of the "Add Document" dialog.
 *
 * @param {Object} props - The component props.
 * @param {import("../../hooks/useLogicDialogAddDocument").DropzoneState} props.dz - The dropzone state object.
 * @param {boolean} [props.showAdditionalInformation=true] - Flag to show additional information section.
 * @returns {JSX.Element} The rendered component.
 */
const DialogAddDocumentContent = ({ dz, showAdditionalInformation = true }) => {
    const [errors, setErrors] = React.useState(null);

    const { control } = useFormContext();

    const { data: list_employees, isLoading } = useQuery(
        ["list_employees"],
        () => {
            return _api_get_employees();
        },
        {
            enabled: showAdditionalInformation,
        }
    );

    const { data: list_categories, isLoading: is_loading_categories } =
        useQuery(
            ["list_categories"],
            () => {
                return _api_get_categories();
            },
            {
                enabled: showAdditionalInformation,
            }
        );

    const format_list = list_employees
        ?.filter((user) => !user?.is_department)
        ?.map((user) => ({
            key: user.id,
            header: user.name,
            image: {
                src: user?.is_department
                    ? AVATAR_DEPARTMENT_DEFAULT
                    : user.avatar
                    ? user.avatar
                    : AVATAR_DEFAULT,
                onError: ({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src = AVATAR_DEFAULT;
                },
            },
            content: user.job_title,
        }));

    const format_list_categories = list_categories?.map((user) => ({
        key: user.id,
        header: user.name,
        image: user.icon_url,
    }));

    return (
        <Flex column gap="gap.small">
            {/* description */}
            <Flex>
                <Description
                    content={
                        <p>
                            Vous pouvez importer{" "}
                            <span className="bold">
                                jusqu'à 5 fichiers simultanément.
                            </span>
                        </p>
                    }
                />
            </Flex>
            {/* alertes */}
            {errors && (
                <Flex>
                    <Alert
                        visible={true}
                        icon={<ExclamationTriangleIcon />}
                        content={errors}
                        warning={true}
                        dismissible={true}
                        dismissAction={{
                            title: "Fermer",
                            onClick: () => setErrors(null),
                        }}
                        styles={{
                            width: "100%",
                        }}
                    />
                </Flex>
            )}
            <Flex column gap="gap.smaller">
                {/* affichage des documents */}
                <Flex column gap="gap.small">
                    {dz.files.map((file) => {
                        const progressFile = dz.progress[file.name];
                        const format_size = file_size_format(file.size);
                        const ext = ICON_FROM_EXTENSION(file.name);
                        return (
                            <Attachment
                                key={file.name}
                                header={file.name}
                                icon={ext}
                                actionable
                                progress={
                                    progressFile === 100 ? 0 : progressFile
                                }
                                description={format_size}
                                action={{
                                    icon: <CloseIcon />,
                                    onClick: () => dz.removeFile(file.name),
                                    title: "Supprimer",
                                }}
                                inverted
                                fluid
                                styles={{
                                    minWidth: "100%",
                                }}
                            />
                        );
                    })}
                </Flex>
                {/* dropzone */}
                <DropzoneGlobal
                    dz={dz}
                    onErrors={(errors) => {
                        return setErrors(errors);
                    }}
                />
                {/* footer dropzone */}
                <Flex space="between" vAlign="center">
                    <Flex gap="gap.small" vAlign="center">
                        <SupportedFormat content={"Formats supportés"} />
                        <Tooltip
                            trigger={<QuestionCircleIcon outline />}
                            subtle={false}
                            pointing
                            position={"after"}
                            align="bottom"
                            content={
                                <Flex column gap="gap.medium">
                                    <ExtensionTitle content={"PDFs"} />
                                    <Flex column>
                                        <ExtensionTitle content={"Images"} />
                                        <Extensions
                                            content={
                                                "(JPEG, PNG, TIFF, HEIC,GIF, WEBP, SVG, TGA, EPS)"
                                            }
                                        />
                                    </Flex>
                                    <Flex column>
                                        <ExtensionTitle
                                            content={"Office files"}
                                        />
                                        <Extensions
                                            content={
                                                "(DOC, DOCX, XLS, XLSX,PPT, PPS, PPTX, PPSX, RTF, ODT)"
                                            }
                                        />
                                    </Flex>
                                </Flex>
                            }
                        />
                    </Flex>
                    <Flex>
                        <MaximalSizeFile content={"Poids maximal : 5 Mo"} />
                    </Flex>
                </Flex>
            </Flex>
            {showAdditionalInformation && (
                <>
                    {/* hr */}
                    <div>
                        <Divider size={1} />
                    </div>
                    {/* Informations complémentaires */}
                    <Flex gap="gap.smaller" vAlign="center">
                        <AdditionalInformation
                            content={
                                <p>
                                    Informations complémentaires{" "}
                                    <span className="red">*</span>
                                </p>
                            }
                        />
                    </Flex>
                    {/* formulaire */}
                    <Flex column gap="gap.small">
                        <Flex vAlign="center" gap="gap.small">
                            <InputLabel content="Personne concernée par cet ajout" />
                            <Flex>
                                {isLoading ? (
                                    <Skeleton animation="pulse">
                                        <Skeleton.Input width="300px" />
                                    </Skeleton>
                                ) : (
                                    <Controller
                                        control={control}
                                        name="user"
                                        rules={{
                                            required: {
                                                value: true,
                                                message:
                                                    "Ce champ est obligatoire",
                                            },
                                        }}
                                        render={({
                                            field: {
                                                onChange,
                                                value,
                                                name,
                                                ref,
                                            },
                                        }) => {
                                            const find_value =
                                                format_list?.findIndex(
                                                    (user) =>
                                                        user.key === value?.key
                                                ) ?? -1;
                                            return (
                                                <DropdownStyled
                                                    items={format_list}
                                                    value={
                                                        find_value !== -1
                                                            ? format_list.at(
                                                                  find_value
                                                              )
                                                            : null
                                                    }
                                                    ref={ref}
                                                    name={name}
                                                    onChange={(e, i) => {
                                                        return onChange(
                                                            i.value
                                                        );
                                                    }}
                                                    placeholder="Choisir dans la liste"
                                                    checkable
                                                    search
                                                />
                                            );
                                        }}
                                    />
                                )}
                            </Flex>
                        </Flex>
                        <Flex vAlign="center" gap="gap.small">
                            <InputLabel content="Catégorie du document" />
                            <div>
                                {is_loading_categories ? (
                                    <Skeleton animation="pulse">
                                        <Skeleton.Input width="300px" />
                                    </Skeleton>
                                ) : (
                                    <Controller
                                        control={control}
                                        name="category"
                                        rules={{
                                            required: {
                                                value: true,
                                                message:
                                                    "Ce champ est obligatoire",
                                            },
                                        }}
                                        render={({
                                            field: {
                                                onChange,
                                                value,
                                                name,
                                                ref,
                                            },
                                        }) => {
                                            const find_value =
                                                format_list_categories?.findIndex(
                                                    (cat) =>
                                                        cat.key === value?.key
                                                ) ?? -1;
                                            return (
                                                <Dropdown
                                                    items={
                                                        format_list_categories
                                                    }
                                                    value={
                                                        find_value !== -1
                                                            ? format_list_categories.at(
                                                                  find_value
                                                              )
                                                            : null
                                                    }
                                                    onChange={(e, i) => {
                                                        return onChange(
                                                            i.value
                                                        );
                                                    }}
                                                    ref={ref}
                                                    name={name}
                                                    placeholder="Aucune par défaut"
                                                    checkable
                                                />
                                            );
                                        }}
                                    />
                                )}
                            </div>
                        </Flex>
                    </Flex>
                </>
            )}
        </Flex>
    );
};

export default DialogAddDocumentContent;

const DropdownStyled = styled(Dropdown)`
    ul {
        border: 1px solid #efefef;
        border-radius: 4px;
    }
`;

const AdditionalInformation = styled(Text)`
    p {
        color: var(
            --light-theme-rest-foreground-default-foreground-242424,
            #242424
        );

        font-size: 18px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px; /* 133.333% */
    }
    span.red {
        color: var(--color-danger);
    }
`;

const Extensions = styled(Text)`
    color: var(--Light-theme-Rest-Foreground-Default-Foreground-3, #fff);

    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
`;

const MaximalSizeFile = styled(Text)`
    color: var(--Light-theme-Rest-Foreground-Default-Foreground-2, #616161);
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px; /* 133.333% */
`;

const SupportedFormat = styled(Text)`
    color: var(
        --light-theme-rest-foreground-default-foreground-1424242,
        #424242
    );
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 17px; /* 141.667% */
`;

const ExtensionTitle = styled(Text)`
    color: var(--Light-theme-Rest-Foreground-Default-Foreground-3, #fff);

    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 16px; /* 133.333% */
`;

const Description = styled(Text)`
    p {
        color: var(
            --light-theme-rest-foreground-default-foreground-1424242,
            #424242
        );

        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px; /* 142.857% */
        span.bold {
            font-weight: 600;
        }
    }
`;
